/**
 * Inactivity Context for managing inactivity timeout warnings and auto-logout.
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "./AuthContext";
import { getSessionSettings } from "../api/generated";
import { setupActivityInterceptor, removeActivityInterceptor } from "../api/interceptors";

interface SessionSettings {
  inactivity_enabled: boolean;
  inactivity_timeout_minutes: number;
  warning_period_minutes: number;
}

interface InactivityContextValue {
  isWarningVisible: boolean;
  remainingSeconds: number;
  resetTimer: () => void;
  dismissWarning: () => void;
}

const InactivityContext = createContext<InactivityContextValue | undefined>(undefined);

const DEFAULT_SETTINGS: SessionSettings = {
  inactivity_enabled: true,
  inactivity_timeout_minutes: 30,
  warning_period_minutes: 2,
};

const ACTIVITY_EVENT = "user-activity";
const BROADCAST_CHANNEL_NAME = "inactivity-sync";
const ACTIVITY_DEBOUNCE_MS = 100;

export const InactivityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, signOut } = useAuth();
  const [settings, setSettings] = useState<SessionSettings>(DEFAULT_SETTINGS);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const lastActivityRef = useRef<number>(Date.now());
  const timeoutIdRef = useRef<number | null>(null);
  const warningIntervalRef = useRef<number | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const debounceTimerRef = useRef<number | null>(null);

  // Fetch settings on mount
  useEffect(() => {
    if (!user) return;

    const fetchSettings = async () => {
      try {
        // The orval mutator returns the response body directly
        const data = (await getSessionSettings()) as unknown as SessionSettings;
        setSettings(data);
      } catch (error) {
        console.error("Failed to fetch session settings:", error);
        // Use defaults on error
      }
    };

    fetchSettings();
  }, [user]);

  // Calculate timeout values in milliseconds
  const timeoutMs = settings.inactivity_timeout_minutes * 60 * 1000;
  const warningMs = settings.warning_period_minutes * 60 * 1000;

  const clearTimers = useCallback(() => {
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (warningIntervalRef.current) {
      window.clearInterval(warningIntervalRef.current);
      warningIntervalRef.current = null;
    }
    if (debounceTimerRef.current) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const handleTimeout = useCallback(() => {
    setIsWarningVisible(false);
    clearTimers();
    signOut();
  }, [signOut, clearTimers]);

  const showWarning = useCallback(() => {
    const remaining = Math.ceil(warningMs / 1000);
    setRemainingSeconds(remaining);
    setIsWarningVisible(true);

    // Update countdown every second
    warningIntervalRef.current = window.setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningMs, handleTimeout]);

  const startTimer = useCallback(() => {
    clearTimers();

    if (!settings.inactivity_enabled) return;

    const warningTime = timeoutMs - warningMs;

    timeoutIdRef.current = window.setTimeout(() => {
      showWarning();
    }, warningTime);
  }, [settings.inactivity_enabled, timeoutMs, warningMs, clearTimers, showWarning]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsWarningVisible(false);
    startTimer();

    // Broadcast to other tabs
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: "activity",
        timestamp: Date.now(),
      });
    }
  }, [startTimer]);

  const dismissWarning = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  // Handle activity events with debouncing
  const handleActivity = useCallback(() => {
    if (debounceTimerRef.current) return;

    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      resetTimer();
    }, ACTIVITY_DEBOUNCE_MS);
  }, [resetTimer]);

  // Handle visibility change - recalculate timer when tab becomes visible
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    if (!settings.inactivity_enabled) return;

    const elapsed = Date.now() - lastActivityRef.current;
    const remaining = timeoutMs - elapsed;

    if (remaining <= 0) {
      handleTimeout();
    } else if (remaining <= warningMs) {
      const warningRemaining = Math.ceil(remaining / 1000);
      setRemainingSeconds(warningRemaining);
      setIsWarningVisible(true);

      if (warningIntervalRef.current) {
        window.clearInterval(warningIntervalRef.current);
      }

      warningIntervalRef.current = window.setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      startTimer();
    }
  }, [settings.inactivity_enabled, timeoutMs, warningMs, handleTimeout, startTimer]);

  // Set up event listeners and broadcast channel
  useEffect(() => {
    if (!user || !settings.inactivity_enabled) {
      clearTimers();
      return;
    }

    // Set up activity interceptor
    setupActivityInterceptor();

    // Listen for activity events from axios interceptor
    window.addEventListener(ACTIVITY_EVENT, handleActivity);

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Set up cross-tab sync via BroadcastChannel
    let storageHandler: ((event: StorageEvent) => void) | null = null;
    try {
      broadcastChannelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      broadcastChannelRef.current.onmessage = (event) => {
        if (event.data.type === "activity") {
          lastActivityRef.current = event.data.timestamp;
          setIsWarningVisible(false);
          startTimer();
        }
      };
    } catch {
      // BroadcastChannel not supported, fallback to localStorage
      storageHandler = (event: StorageEvent) => {
        if (event.key === "inactivity-last-activity") {
          const timestamp = parseInt(event.newValue || "0", 10);
          if (timestamp > 0) {
            lastActivityRef.current = timestamp;
            setIsWarningVisible(false);
            startTimer();
          }
        }
      };
      window.addEventListener("storage", storageHandler);
    }

    // Start the timer
    startTimer();

    return () => {
      clearTimers();
      removeActivityInterceptor();
      window.removeEventListener(ACTIVITY_EVENT, handleActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (storageHandler) {
        window.removeEventListener("storage", storageHandler);
      }

      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [
    user,
    settings.inactivity_enabled,
    handleActivity,
    handleVisibilityChange,
    startTimer,
    clearTimers,
  ]);

  return (
    <InactivityContext.Provider
      value={{
        isWarningVisible,
        remainingSeconds,
        resetTimer,
        dismissWarning,
      }}
    >
      {children}
    </InactivityContext.Provider>
  );
};

export const useInactivity = () => {
  const context = useContext(InactivityContext);
  if (context === undefined) {
    throw new Error("useInactivity must be used within an InactivityProvider");
  }
  return context;
};
